import { Stack, Paper, Title, Group, Box, Text, ScrollArea } from "@mantine/core";
import { useStatusColors } from "../../hooks/useStatusColors";
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";

interface StatusLegendProps {
  /**
   * Whether to show the legend in a paper container with border
   */
  withPaper?: boolean;
}

/**
 * StatusLegend displays a color-coded legend showing all geometry statuses
 * and their corresponding colors on the map
 */
export default function StatusLegend({ withPaper = true }: StatusLegendProps) {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 309, addressSystemId: addrIdFromStore || 0, autoLoad: true });

  const statusColors = useStatusColors();

  const legendContent = (
    <Stack gap="sm">      
      {statusColors.length === 0 ? (
        <Text size="xs" c="dimmed" fs="italic">
          {getTranslation("LABEL_STATUS_LEGEND_NO_STATUSES_AVAILABLE") || "No statuses available"}
        </Text>
      ) : (
        <ScrollArea.Autosize mah={300} offsetScrollbars scrollbarSize={8}>
          <Stack gap="xs">
            {statusColors.map((status) => (
              <Group key={status.statusId} gap="xs" wrap="nowrap">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    backgroundColor: status.color,
                    border: "1px solid #ccc",
                    borderRadius: 3,
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {status.statusDescription}
                  </Text>
                  {status.statusDescription && status.statusDescription !== status.statusName && (
                    <Text size="xs" c="dimmed" truncate>
                      {status.statusName}
                    </Text>
                  )}
                </div>
              </Group>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      )}
    </Stack>
  );

  if (withPaper) {
    return (
      <Paper p="sm" withBorder shadow="xs">
        {legendContent}
      </Paper>
    );
  }

  return legendContent;
}
